import cn from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import Intercom from '@disco/api/intercom'
import { getTrackPlaying } from '@disco/state-management/selectors/media'
import KeyboardShortcutsModal from 'components/keyboard-shortcuts-modal'
import Icon from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'
import { Badge, DropdownMenu, Flex, Text, Theme } from 'components/ds'
import { LuLifeBuoy } from '@react-icons/all-files/lu/LuLifeBuoy'
import { LuBookOpen } from '@react-icons/all-files/lu/LuBookOpen'
import { LuGraduationCap } from '@react-icons/all-files/lu/LuGraduationCap'
import { LuHeadphones } from '@react-icons/all-files/lu/LuHeadphones'
import { LuBuilding2 } from '@react-icons/all-files/lu/LuBuilding2'
import { LuKeyboard } from '@react-icons/all-files/lu/LuKeyboard'
import { LuGift } from '@react-icons/all-files/lu/LuGift'
import styles from './index.module.css'

const HelpMenu = (): React.ReactNode => {
  const playerVisible = useSelector(getTrackPlaying)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState(false)
  const intercomMessageContainer = useRef<MutationObserver | null | undefined>(
    null,
  )
  const [isIntercomOpen, setIsIntercomOpen] = useState(false)
  const [isIntercomMessageOpen, setIsIntercomMessageOpen] =
    useState<boolean>(false)
  const [isIntercomAvailable, setIsIntercomAvailable] = useState(() =>
    Intercom.isAvailable(),
  )

  useEffect(() => {
    Intercom.addIsOpenCallback(setIsIntercomOpen)
    const onAvailable = () => setIsIntercomAvailable(true)
    const onShutdown = () => setIsIntercomAvailable(false)
    Intercom.addAvailableCallback(onAvailable)
    Intercom.addShutdownCallback(onShutdown)
    return () => {
      Intercom.removeIsOpenCallback(setIsIntercomOpen)
      Intercom.removeAvailableCallback(onAvailable)
      Intercom.removeShutdownCallback(onShutdown)
    }
  }, [])

  useEffect(() => {
    const checkIntercomMessagePresence = () => {
      const intercomBorderlessMessage = document.querySelector(
        '.intercom-borderless-frame',
      )
      setIsIntercomMessageOpen(intercomBorderlessMessage !== null)
    }

    intercomMessageContainer.current = new MutationObserver(
      checkIntercomMessagePresence,
    )

    if (document.body != null) {
      intercomMessageContainer.current.observe(document.body, {
        childList: true,
        subtree: true,
      })
      checkIntercomMessagePresence()
    }

    return () => {
      if (intercomMessageContainer.current) {
        intercomMessageContainer.current.disconnect()
      }
    }
  }, [])

  const toggleShortcutsOpen = () =>
    setShortcutsModalOpen(prevState => !prevState)

  const handleTriggerClick = () => {
    if (isIntercomOpen) {
      Intercom.hideWidget()
    } else if (isIntercomMessageOpen) {
      Intercom.openHome()
    }
  }

  const openExternal = (url: string) =>
    window.open(url, '_blank', 'noopener,noreferrer')

  return (
    <>
      <div
        className={cn(styles.helpMenu, {
          [styles.helpMenuPlayer]: playerVisible,
        })}
      >
        <Theme appearance="light" hasBackground={false}>
          <DropdownMenu.Root
            open={isIntercomOpen || isIntercomMessageOpen ? false : openMenu}
            onOpenChange={open => {
              if (!isIntercomOpen && !isIntercomMessageOpen) {
                setOpenMenu(open)
              }
            }}
          >
            <DropdownMenu.Trigger>
              <button
                type="button"
                className={styles.helpMenuButton}
                onClick={handleTriggerClick}
              >
                <Tooltip
                  content="Help and resources"
                  position="top"
                  hidden={openMenu}
                >
                  <span className={styles.helpMenuIconWrapper}>
                    <Icon
                      type="help"
                      width="55"
                      height="55"
                      className={styles.helpMenuIcon}
                    />
                  </span>
                </Tooltip>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content
              variant="soft"
              side="top"
              align="end"
              sideOffset={8}
            >
              <DropdownMenu.Label>Learn</DropdownMenu.Label>

              <DropdownMenu.Item
                onClick={() => {
                  dispatch(sendMixpanelEvent('Open support center'))
                  openExternal('https://support.disco.ac/home')
                }}
              >
                <Flex align="center" gap="2">
                  <LuLifeBuoy size={16} />
                  Support center
                </Flex>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onClick={() => {
                  dispatch(sendMixpanelEvent('Open getting started guide'))
                  openExternal('https://school.disco.ac/handbook/new-to-disco')
                }}
              >
                <Flex align="center" gap="2">
                  <LuBookOpen size={16} />
                  Getting started
                </Flex>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onClick={() => {
                  dispatch(sendMixpanelEvent('Open school of DISCO'))
                  openExternal('https://school.disco.ac')
                }}
              >
                <Flex align="center" gap="2" width="100%">
                  <LuGraduationCap size={16} />
                  <Text className={styles.menuItemLabel}>School of DISCO</Text>
                  <Badge size="1" color="blue" radius="full">
                    New
                  </Badge>
                </Flex>
              </DropdownMenu.Item>

              <DropdownMenu.Separator />

              <DropdownMenu.Label>Contact</DropdownMenu.Label>

              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger>
                  <Flex align="center" gap="2">
                    <LuHeadphones size={16} />
                    Contact support
                  </Flex>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent>
                  <DropdownMenu.Item
                    onClick={() => navigate('/contact-support')}
                  >
                    Email support
                  </DropdownMenu.Item>
                  {isIntercomAvailable && (
                    <DropdownMenu.Item
                      onClick={() => {
                        Intercom.openHome()
                        dispatch(sendMixpanelEvent('Open contact support'))
                      }}
                    >
                      Chat to support
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>

              <DropdownMenu.Item onClick={() => navigate('/contact-sales')}>
                <Flex align="center" gap="2">
                  <LuBuilding2 size={16} />
                  Contact sales
                </Flex>
              </DropdownMenu.Item>

              <DropdownMenu.Separator />

              <DropdownMenu.Item shortcut="?" onClick={toggleShortcutsOpen}>
                <Flex align="center" gap="2">
                  <LuKeyboard size={16} />
                  Keyboard shortcuts
                </Flex>
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onClick={() => navigate('/referral-invite/send')}
              >
                <Flex align="center" gap="2">
                  <LuGift size={16} />
                  Refer a friend
                </Flex>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Theme>
      </div>

      <KeyboardShortcutsModal
        isOpen={shortcutsModalOpen}
        onClose={toggleShortcutsOpen}
      />
    </>
  )
}

export default HelpMenu
